import addressApi, { MUTATION_SYNC_UNITS } from "api/landlord/address";
import { useEffect, useMemo, useState } from "react";
import { Input, InputSize, Show } from "shared/components";
import { getErrorMessage } from "utils/error";
import { AddUnitModal } from "./components/AddUnitModal";
import { useAuthContext } from "providers/Auth";
import { UnitsTable } from "./components/UnitsTable";
// import { InviteApplicantsModal } from "shared/app/InviteApplicantsModal";
import { DeleteUnitModal } from "./components/DeleteUnitModal";
import { LayoutButtons } from "./components/LayoutButtons";
import { Pagination } from "shared/components/Pagination";
import { useUnitsTableController } from "./controller";
import { PropertyManagementSystemSync } from "shared/components/PropertyManagementSystemSync";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Tabs } from "shared/components/Tabs";
import { cx } from "shared/utils";
import { useMedia } from "shared/providers";
import { Unit } from "types/landlord/address";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { InviteAndCreateModal } from "../InviteAndCreateModal";
import { SortingState } from "@tanstack/react-table";
import { Property } from "shared/types";

const DEFAULT_QUERY = "/portal/v1/units";

enum UnitTabs {
  ALL = "All",
  ALLOWED = "Applications allowed",
  DISALLOWED = "Applications disallowed"
}

export const UnitsTableView = ({
  property,
  query = DEFAULT_QUERY,
  buttons,
  propertyId,
  showAddUnitModal: _showAddUnitModal
}: {
  property?: Property;
  query?: string;
  buttons?: React.ReactNode;
  propertyId?: number;
  showAddUnitModal?: boolean;
}) => {
  const { units, Unit, Units } = useUnitsPropertiesDictionary();
  const { isMobile } = useMedia();
  const { type } = useAuthContext();
  const [showDeleteUnitModal, setShowDeleteUnitModal] =
    useState<boolean>(false);
  const [showAddUnitModal, setShowAddUnitModal] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<UnitTabs>(UnitTabs.ALL);
  const [showInviteApplicantsModal, setShowInviteApplicantsModal] =
    useState<boolean>(false);
  const [selectedUnitId, setSelectedUnitId] = useState<number>();
  const [isSyncing, setIsSyncing] = useState(type.isPMS);
  const [unitsData, setUnitsData] = useState<Unit[] | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const {
    data,
    refetch,
    debouncedParams,
    searchParams,
    setSearchParams,
    queryProperties,
    isFetching,
    isRefetching,
    syncData,
    syncError,
    searchTerm,
    setSearchTerm
  } = useUnitsTableController({
    query,
    isSyncing,
    available_for_application:
      currentTab === UnitTabs.ALLOWED
        ? true
        : currentTab === UnitTabs.DISALLOWED
          ? false
          : null,
    sorting
  });

  const handleAvailableForApplication = (unit: Unit) => {
    setUnitsData((prev) => {
      const temp = prev || [];
      return temp.map((u) => (unit.id === u.id ? { ...u, ...unit } : u));
    });
  };

  useEffect(() => {
    if (data) setUnitsData(data.items);
  }, [data]);

  useEffect(() => {
    if (typeof _showAddUnitModal === "boolean")
      setShowAddUnitModal(_showAddUnitModal);
  }, [_showAddUnitModal]);

  const deleteUnit = async () => {
    try {
      await addressApi.deleteUnit(selectedUnitId || 0);

      toast.success(`${Unit} was deleted successfully`);

      await refetch();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
    setShowDeleteUnitModal(false);
  };

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.info("Link copied");
    } catch (err) {
      toast.error(`Error copying link: ${getErrorMessage(err)}`);
    }
  };

  const { mutate: handleRequestSync } = useMutation({
    mutationFn: async () => {
      return addressApi
        .requestPropertyManagementSystemSync("units")
        .then(() => {
          toast.success(`Sync ${units} requested successfully`);
          setIsSyncing(true);
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
          setIsSyncing(false);
        });
    },
    mutationKey: [MUTATION_SYNC_UNITS]
  });

  useEffect(() => {
    setIsSyncing(type.isPMS);
  }, [type.isPMS]);

  useEffect(() => {
    const isSyncRunning = syncData?.status === "running";
    setIsSyncing(isSyncRunning);

    if (!isSyncRunning) refetch();
  }, [syncData, syncError]);

  useEffect(() => {
    setSearchParams({
      ...searchParams,
      page: "1"
    });
    refetch();
  }, [currentTab]);

  const { propertyIdProp, propertyName, unitName } = useMemo(() => {
    const propertyIdProp = propertyId
      ? propertyId
      : data?.items.find((u) => u.id === selectedUnitId)?.property.id;

    const propertyName =
      data?.items.find((u) => u.id === selectedUnitId)?.property.name ||
      data?.items.find((u) => u.id === selectedUnitId)?.property.address
        .address1;

    const unitName = data?.items.find((u) => u.id === selectedUnitId)?.title;

    return { propertyIdProp, propertyName, unitName };
  }, [propertyId, selectedUnitId, data]);

  return (
    <div className="animate-appear pb-[73px] md:pb-0 w-full">
      <div className="pb-4 w-full">
        <Show if={query === "/portal/v1/units"}>
          <p className="m-0 mb-6 text-2xl font-bold text-content-1000">
            {Units}
          </p>
        </Show>
        <div className="flex justify-between w-full flex-wrap md:flex-nowrap md:max-w-[calc(100vw-120px)]">
          <div className="flex w-full gap-2 items-center">
            <Input
              id="search"
              label=""
              placeholder={`Search ${units}`}
              value={searchParams.unit}
              onChange={(e) =>
                setSearchParams({
                  ...searchParams,
                  unit: e.target.value,
                  page: "1"
                })
              }
              className="!mb-0 w-full max-w-[280px] h-12 md:h-10"
              size={InputSize.SM}
            />

            <PropertyManagementSystemSync
              isSyncing={isSyncing}
              handleRequestSync={handleRequestSync}
              lastSuccessSyncedAt={syncData?.last_successful_sync}
            />
          </div>

          <div
            className={cx(
              isMobile && !buttons ? "hidden" : "flex",
              "gap-2 ml-auto md:w-auto w-full flex-nowrap pt-4 md:pt-0"
            )}
          >
            {buttons ? (
              buttons
            ) : (
              <LayoutButtons
                setShowAddUnitModal={setShowAddUnitModal}
                handleCopyLink={handleCopyLink}
              />
            )}
          </div>
        </div>

        <Show if={type.isBoomScreen}>
          <div className="md:w-fit mt-4">
            <Tabs
              setActiveTab={(e) => setCurrentTab(e)}
              selected={currentTab}
              tabs={[UnitTabs.ALL, UnitTabs.ALLOWED, UnitTabs.DISALLOWED]}
            />
          </div>
        </Show>
      </div>
      <UnitsTable
        currentProperty={property}
        handleAvailableForApplication={handleAvailableForApplication}
        data={unitsData || data?.items || []}
        handleCopyLink={handleCopyLink}
        hasSearchParams={!!debouncedParams}
        isLoading={isFetching && !isRefetching}
        setSelectedUnitId={setSelectedUnitId}
        setShowInviteApplicantsModal={setShowInviteApplicantsModal}
        setShowDeleteUnitModal={setShowDeleteUnitModal}
        hideProperty={query !== DEFAULT_QUERY}
        sorting={sorting}
        setSorting={setSorting}
        sortableKeys={["title"]}
      />
      <div className="ml-auto mt-4 flex md:max-w-[calc(100vw-106px)]">
        <Pagination
          className="md:justify-end justify-center"
          total={data?.page_count || 0}
          current={Number(searchParams.page)}
          onChange={(p) =>
            setSearchParams({ ...searchParams, page: String(p) })
          }
        />
      </div>
      <DeleteUnitModal
        opened={showDeleteUnitModal}
        onClose={() => setShowDeleteUnitModal(false)}
        onDelete={deleteUnit}
      />
      <AddUnitModal
        propertiesMeta={queryProperties}
        opened={showAddUnitModal}
        onClose={() => setShowAddUnitModal(false)}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      <Show
        if={
          Boolean(selectedUnitId) && showInviteApplicantsModal && propertyIdProp
        }
      >
        {/* <InviteApplicantsModal
          opened={showInviteApplicantsModal}
          onClose={() => setShowInviteApplicantsModal(false)}
          propertyId={propertyIdProp}
          propertyName={propertyName}
          unitId={selectedUnitId}
          unitName={unitName}
          queryKey={[
            query,
            {
              params: {
                page: searchParams.page,
                search: debouncedParams,
                per_page: 10
              }
            }
          ]}
        /> */}
        <InviteAndCreateModal
          open={showInviteApplicantsModal}
          setOpen={setShowInviteApplicantsModal}
          unitId={selectedUnitId}
          propertyId={propertyIdProp}
          propertyName={propertyName}
          unitName={unitName}
          onInviteRefreshKey={[
            query,
            {
              params: {
                page: searchParams.page,
                search: debouncedParams,
                per_page: 10
              }
            }
          ]}
          unitSelectSkipped={false}
        />
      </Show>
    </div>
  );
};
